import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTrance } from '../components/TranceContext';
import ModeSelectionModal from '../components/ModeSelectionModal';

const NewTrancePage = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const navigate = useNavigate();
  const { isTranceMode, toggleTranceMode } = useTrance();
  const hasMountedRef = useRef(false);
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);

  // Start audio when entering trance mode
  useEffect(() => {
    if (!hasMountedRef.current && !isTranceMode) {
      toggleTranceMode();
      hasMountedRef.current = true;
    }
    
    // Turn off audio when leaving
    return () => {
      if (isTranceMode) {
        toggleTranceMode();
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width, height, gridSize;

    const config = {
      gridCount: 10,
      speed: 0.0025,
      phaseStrength: 0.05,
      lineWidth: 1,
      pauseDuration: 0.5,
      colorSpeed: 0.0005 // Speed of color cycling
    };

    // Color cycling function
    function getColor(time, offset = 0) {
      const hue = ((time * config.colorSpeed * 360) + offset) % 360;
      return `hsl(${hue}, 80%, 60%)`;
    }

    function ease(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function init() {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      gridSize = width / config.gridCount / 2;
    }

    function drawCell(x, y, size, progress, colorTime, distOffset) {
      const half = size / 2;
      
      // Get color based on time and distance for rainbow effect
      const color = getColor(colorTime, distOffset * 30);
      
      ctx.beginPath();
      ctx.rect(x - half, y - half, size, size);
      ctx.strokeStyle = color;
      ctx.lineWidth = config.lineWidth;
      ctx.stroke();

      if (progress > 0.01) {
        ctx.beginPath();
        ctx.moveTo(x, y - half);
        ctx.lineTo(x, y + half);
        ctx.moveTo(x - half, y);
        ctx.lineTo(x + half, y);
        
        // Use rgba with the color
        const rgb = hslToRgb(((colorTime * config.colorSpeed * 360) + distOffset * 30) % 360, 80, 60);
        ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${progress})`;
        ctx.stroke();
      }
    }

    // Helper function to convert HSL to RGB
    function hslToRgb(h, s, l) {
      s /= 100;
      l /= 100;
      const k = n => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return {
        r: Math.round(255 * f(0)),
        g: Math.round(255 * f(8)),
        b: Math.round(255 * f(4))
      };
    }

    let time = 0;

    function animate() {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;

      const cols = config.gridCount + 4;
      const rows = Math.ceil(height / gridSize) + 4;

      for (let i = -cols / 2; i <= cols / 2; i++) {
        for (let j = -rows / 2; j <= rows / 2; j++) {
          const x0 = i * gridSize;
          const y0 = j * gridSize;
          const dist = Math.sqrt(i * i + j * j);

          let t = (time - dist * config.phaseStrength) % 1;
          if (t < 0) t += 1;

          const easedT = ease(t);
          const scale = 1 / Math.pow(2, easedT);

          const x = cx + x0 * scale;
          const y = cy + y0 * scale;
          const size = gridSize * scale;

          if (x > -size && x < width + size && y > -size && y < height + size) {
            drawCell(x, y, size, easedT, time, dist);
          }
        }
      }

      time += config.speed;
      animationRef.current = requestAnimationFrame(animate);
    }

    function handleResize() {
      init();
    }

    window.addEventListener('resize', handleResize);
    init();
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    // Turn off audio before navigating away
    if (isTranceMode) {
      toggleTranceMode();
    }
    navigate('/');
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      <canvas
        ref={canvasRef}
        id="canvas"
        style={{
          display: 'block',
          imageRendering: '-moz-crisp-edges',
          imageRendering: '-webkit-optimize-contrast',
          imageRendering: 'pixelated',
        }}
      />
      
      {/* Buttons */}
      <div className="absolute top-6 right-6 z-50 flex gap-3">
        <Button
          onClick={() => setIsModeModalOpen(true)}
          variant="outline"
          className="bg-black/50 backdrop-blur-sm border-white/20 hover:bg-gradient-to-r hover:from-cyan-500/20 hover:via-purple-500/20 hover:to-pink-500/20 hover:border-white/40 transition-all duration-300 font-heading tracking-wider"
        >
          <Compass className="w-4 h-4 mr-2" />
          CHOOSE YOUR PATH
        </Button>
        
        <Button
          onClick={handleClose}
          variant="outline"
          className="bg-black/50 backdrop-blur-sm border-white/20 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300 font-heading tracking-wider"
        >
          TRANCE MODE OFF
        </Button>
      </div>

      {/* Title Overlay */}
      <div className="absolute bottom-10 left-0 right-0 text-center pointer-events-none z-20">
        <h1 className="text-4xl md:text-6xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-primary/50 tracking-widest opacity-50">
          TRANCE MODE
        </h1>
      </div>

      {/* Mode Selection Modal */}
      <ModeSelectionModal isOpen={isModeModalOpen} onClose={() => setIsModeModalOpen(false)} />
    </div>
  );
};

export default NewTrancePage;
